import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import {
  blockOf,
  hasPromises,
  isCompleted,
  useHandover,
  useMarkChecklist,
  useRecordOutcome,
  useRequestConfirmation,
  type PurchaseOutcome,
} from '@entities/handover';
import { useSearchSession } from '@entities/search-session';
import { KarlDialogue } from '@features/chat';
import {
  AcceptedTermsCard,
  HandoverChecklist,
  HandoverMemo,
  HandshakeCard,
  PurchaseOutcomeForm,
  SellerContactCard,
  SuccessFeeCard,
  WrittenConfirmation,
} from '@features/handover';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, ButtonLink, KarlIntro, Skeleton, StateBlock } from '@shared/ui';

import styles from './HandoverPage.module.css';

/**
 * H-01 «Сопровождение покупки» — шаг 7 воронки (FE-260, MVP_PRD §39).
 *
 * Экран отвечает на один вопрос: **что делать дальше, чтобы довести покупку до
 * конца** — и ни на один сверх него.
 *
 * Шесть правил экрана:
 *
 * * **контакты автохауса и зафиксированные условия — первыми.** Ради них шаг и
 *   существует: пользователь идёт на встречу, и ему нужны адрес и то, о чём
 *   договорились, с цитатами (§39.1);
 * * **два чек-листа, а не один.** «Что проверить лично до покупки» выводится из
 *   признаков внимания, вопросов без ответа и расхождений сверки; «приёмка при
 *   получении» — закрытый список, одинаковый для всех. Разные вопросы в разные
 *   моменты, и сливать их нельзя;
 * * **просьба о письменном подтверждении — только там, где есть что просить**
 *   (§42.3). Кнопка, ведущая к отказу, была бы обещанием отсутствующего
 *   действия (§2);
 * * **памятка — информационный материал, а не юридическая услуга** (§39.2), и
 *   обязательный дисклеймер стоит рядом с ней;
 * * **отметка исхода в два шага, «купил» — с явным перечнем фиксируемого**
 *   (§39.3). Отсутствие отметки никогда не значит покупку;
 * * **ни одного утверждения об автомобиле и о продавце.** Экран не говорит
 *   «автомобиль исправен», «продавец надёжен» и «сделка безопасна»: таких
 *   выводов CarViQ.DE не делает (§2).
 *
 * `404 HANDOVER_NOT_FOUND` — заказ до шага 7 не дошёл. Это **состояние, а не
 * ошибка**: решение по финальному предложению ещё не принято, и экран ведёт
 * туда, где его принимают.
 *
 * Редизайн 2026-10 (DESIGN_SYSTEM §5.1, §7.2): вводный абзац — реплика Karl
 * (`KarlIntro`); код передачи — крупно, моноширинно, на тинте; красная кнопка
 * на экране одна — подтверждение «купил». Просьба о письменном подтверждении —
 * тоже письмо, но не главное действие шага: синий контур.
 */
export function HandoverPage() {
  const { t, i18n } = useTranslation();
  const { searchId = '' } = useParams();
  const locale = i18n.resolvedLanguage ?? 'de';

  const sessionQuery = useSearchSession(searchId || null);
  const handoverQuery = useHandover(searchId || null);
  const mark = useMarkChecklist(searchId);
  const outcome = useRecordOutcome(searchId);
  const confirmation = useRequestConfirmation(searchId);

  const session = sessionQuery.data ?? null;
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: { extra: <KarlDialogue searchId={searchId} /> },
    // Шаг 5 трекера — переписка, в которой принято предложение.
    negotiationId: handoverQuery.data?.negotiation_id ?? null,
  });

  if (handoverQuery.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="45%" />
        <Skeleton height="12rem" radius="var(--radius-card)" />
        <Skeleton height="18rem" radius="var(--radius-card)" />
      </div>
    );
  }

  if (handoverQuery.isError) {
    const error = handoverQuery.error;
    const unpaid = error instanceof ApiError && error.status === 402;
    // Заказ до шага 7 не дошёл — это ожидание, а не сбой: решение по
    // предложению принимается на шаге 6, и экран ведёт именно туда.
    const notReached = error instanceof ApiError && error.code === 'HANDOVER_NOT_FOUND';

    if (notReached) {
      return (
        <div className={styles.page}>
          <StateBlock
            tone="neutral"
            title={t('handover.notReached.title')}
            description={t('handover.notReached.text')}
            action={
              <ButtonLink to={ROUTES.searchConclusion(searchId)}>
                {t('handover.notReached.action')}
              </ButtonLink>
            }
          />
        </div>
      );
    }

    return (
      <div className={styles.page}>
        <StateBlock
          tone={unpaid ? 'neutral' : 'error'}
          title={unpaid ? t('selection.locked.title') : t('handover.error.title')}
          description={unpaid ? t('selection.locked.text') : t('handover.error.text')}
          action={
            unpaid ? (
              <ButtonLink to={ROUTES.searchPayment(searchId)}>
                {t('selection.locked.action')}
              </ButtonLink>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  void handoverQuery.refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            )
          }
        />
      </div>
    );
  }

  const state = handoverQuery.data;
  const completed = isCompleted(state);
  const orderTitle = session?.session.title ?? null;

  return (
    <div className={styles.page}>
      <Link to={ROUTES.searchConclusion(searchId)} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('handover.backToConclusion')}
      </Link>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('handover.title')}</h1>
        {/* Вводный абзац — реплика Karl (DESIGN_SYSTEM §5.4, §7.2): текст
            фиксированный, из локалей, LLM его не пишет. */}
        <KarlIntro>{t('handover.subtitle')}</KarlIntro>
      </header>

      <SellerContactCard contact={state.contact} orderTitle={orderTitle} />

      {/* Код передачи стоит сразу за контактами: он нужен ровно в тот момент,
          когда автохаус напишет покупателю (§39.4). */}
      <HandshakeCard state={state} />

      <AcceptedTermsCard terms={state.terms} locale={locale} />

      <HandoverChecklist
        block={blockOf(state, 'pre_purchase')}
        readOnly={completed}
        pending={mark.isPending}
        onToggle={(key, done) => {
          mark.mutate([{ key, done }]);
        }}
      />

      <HandoverChecklist
        block={blockOf(state, 'acceptance')}
        readOnly={completed}
        pending={mark.isPending}
        onToggle={(key, done) => {
          mark.mutate([{ key, done }]);
        }}
      />

      {/* Просить нечего — блока нет вовсе: кнопка, ведущая к отказу, обещает
          действие, которого не существует (§2, §42.3). Завершённый заказ писем
          не шлёт. */}
      {hasPromises(state) && !completed ? (
        <WrittenConfirmation
          state={state}
          pending={confirmation.isPending}
          error={confirmation.error}
          onRequest={() => {
            confirmation.mutate();
          }}
        />
      ) : null}

      <HandoverMemo memo={state.memo} />

      <PurchaseOutcomeForm
        state={state}
        pending={outcome.isPending}
        error={outcome.error}
        onRecord={(value: PurchaseOutcome, confirmed: boolean) => {
          outcome.mutate({ value, confirmed });
        }}
      />

      {/* Расчёт появляется **после** отметки исхода (§42.4): до неё считать
          нечего, и блока нет вовсе. */}
      <SuccessFeeCard searchId={searchId} outcomeRecorded={state.purchase_outcome !== null} />
    </div>
  );
}
